/**
 * API 錯誤 → 給玩家看的訊息（單一出口）。
 *
 * 🛑 **不要把 `e.message` 直接設進任何會渲染到畫面的 state**。`ApiError` 的
 * constructor 是 `super(opts.msg)`，而 `msg` 來源是後端回的 `msg` 或 http.ts 內部
 * 產生的英文字串（`'Request timed out'` / `'Network error'` / `'HTTP 502'` /
 * `'Unauthorized'`）—— 那些是給工程師看的，不該出現在玩家的全屏錯誤頁上。
 *
 * 用法：
 *   error.value = toUserMessage(e)                                  // 通用
 *   error.value = toUserMessage(e, 'common.errors.verifyFailed')    // 指定 fallback
 *
 * ⚠️ 這裡是在「錯誤發生的當下」就把 key 翻成字串存起來（跟本專案既有作法一致）。
 *    也就是說之後切語系不會回頭改寫已存在的錯誤訊息。要做到 locale-reactive
 *    得改成 store 存 key、由元件 render 時再 `t()`，那是另一次重構。
 */

import { i18n } from '@/i18n'
import { ApiError } from './http'

/**
 * 錯誤分類。
 *   - `timeout` / `network` / `server`：**transient**（暫時性）—— 原樣重送有機會成功
 *   - `unauthorized` / `business` / `unknown`：原因確定，重送不會有不同結果
 */
export type ErrorKind = 'timeout' | 'network' | 'server' | 'unauthorized' | 'business' | 'unknown'

/**
 * 分類一個丟出來的錯誤。
 *
 * http.ts 的約定：逾時與網路錯誤都是 `status: 0` / `statusCode: -1`，
 * 差別只在 msg（`'Request timed out'` vs 其他），所以這裡靠 msg 再分一次。
 */
export const classifyError = (e: unknown): ErrorKind => {
  if (!(e instanceof ApiError)) return 'unknown'
  if (e.status === 0) return e.msg === 'Request timed out' ? 'timeout' : 'network'
  if (e.status === 401) return 'unauthorized'
  if (e.status === 429 || e.status >= 500) return 'server'
  if (e.status >= 400) return 'business'
  // status 200 + 業務錯誤碼
  return 'business'
}

/** 分類 → i18n key（`business` / `unknown` 交給呼叫端給的 fallback） */
const KIND_KEY: Partial<Record<ErrorKind, string>> = {
  timeout: 'common.errors.timeout',
  network: 'common.errors.network',
  server: 'common.errors.server',
  unauthorized: 'common.errors.sessionExpired',
}

/**
 * 把任何錯誤轉成可以直接顯示給玩家的字串。
 *
 * @param e 捕捉到的錯誤
 * @param fallbackKey 分類不到明確原因時要用的 i18n key（預設「載入失敗，請重試」）
 */
export const toUserMessage = (e: unknown, fallbackKey = 'common.errors.loadFailed'): string => {
  const key = KIND_KEY[classifyError(e)] ?? fallbackKey
  return i18n.global.t(key)
}
